import {
  bar,
  blank,
  box,
  hstack,
  lines,
  pad,
  separator,
  spark,
  statusbar,
  table,
  type Column,
  type LayoutFn,
  type Segment,
  vstack,
} from "../src";
import {
  C,
  W,
  SPARK_A,
  SPARK_B,
  SPINNER_FRAMES,
  clockNow,
  clamp,
  nfmt,
  rot,
  setTermLayout,
  toFixed,
  type DemoTermElement,
} from "./shared";

const SERVICES = [
  { name: "api-gateway", up: "14d 03h", baseLoad: 0.42, baseRps: 1247 },
  { name: "auth-service", up: "14d 03h", baseLoad: 0.18, baseRps: 892 },
  { name: "db-primary", up: "31d 07h", baseLoad: 0.67, baseRps: 3401 },
  { name: "cache-redis", up: "23d 11h", baseLoad: 0.35, baseRps: 1587 },
  { name: "worker-queue", up: "07d 12h", baseLoad: 0.89, baseRps: 456 },
];

const EVENTS = [
  { level: "info", msg: "deploy v2.4.1 completed (us-east-1)" },
  { level: "warn", msg: "cache-redis latency spike 340ms" },
  { level: "info", msg: "worker-queue scaled 3 > 5 instances" },
  { level: "info", msg: "db-primary backup 2.4GB completed" },
  { level: "info", msg: "tls cert renewed (*.api.prod)" },
  { level: "err", msg: "auth-service timeout 2.1s (retry ok)" },
  { level: "info", msg: "rate-limit updated 1000 > 1500 rps" },
  { level: "info", msg: "healthcheck all 12 nodes passing" },
];

type ServiceRow = {
  service: string;
  ok: number;
  status: string;
  uptime: string;
  load: string;
  rps: string;
};

const SERVICE_COLUMNS: Column<ServiceRow>[] = [
  { key: "service", title: "SERVICE", width: 18 },
  { key: "status", title: "STATUS", width: 10, color: (_value: unknown, row) => (row.ok ? C.green : C.red) },
  { key: "uptime", title: "UPTIME", width: 10 },
  { key: "load", title: "LOAD", width: 8, align: "right" },
  { key: "rps", title: "REQ/s", width: 8, align: "right" },
];

const timeFmt = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
  hour12: false,
});

function raw(text: string, color?: string, bold?: boolean): LayoutFn {
  return lines([[{ text, color, bold }]]);
}

function buildHeroLayout(tick: number): LayoutFn {
  const spin = SPINNER_FRAMES[tick % SPINNER_FRAMES.length] ?? SPINNER_FRAMES[0]!;
  const pulse = Math.floor(tick / 3);
  const clock = clockNow();

  const cpuPct = clamp(Math.round(48 + Math.sin(pulse / 2) * 22 + Math.cos(pulse / 5) * 8), 6, 96);
  const memUsed = clamp(5.6 + (Math.sin(pulse / 3) + 1) * 2.8, 3.2, 14.9);
  const dskUsed = clamp(220 + (Math.sin(pulse / 5) + 1) * 85, 140, 460);

  const sp1 = rot(SPARK_A, tick).map((value, index) => clamp(value + Math.round(Math.sin((tick + index) / 2.4)), 1, 9));
  const sp2 = rot(SPARK_B, tick + 3).map((value, index) => clamp(value + Math.round(Math.sin((tick + index) / 2.9)), 1, 9));

  const incident = pulse % 28 >= 21 ? "cache-redis" : "";

  const serviceData: ServiceRow[] = SERVICES.map((service, index) => {
    const load = clamp(service.baseLoad * (0.85 + (Math.sin((pulse + index * 2) / 3) + 1) * 0.45), 0.04, 2.1);
    const rps = Math.max(0, Math.round(service.baseRps * (0.68 + (Math.sin((pulse + index * 3) / 4) + 1) * 0.55)));
    const ok = incident === service.name ? 0 : 1;
    return {
      service: service.name,
      ok,
      status: ok ? "\u25cf up" : "\u25cb dn",
      uptime: ok ? service.up : "--",
      load: ok ? toFixed(load, 2) : "--",
      rps: ok ? nfmt(rps) : "--",
    };
  });

  const okSvc = serviceData.filter((service) => service.ok).length;
  const header = pad(` ${spin} 1ch`, W - clock.length - 2) + clock + "  ";

  const eventSlice = Math.floor(tick / 15);
  const nowMs = Date.now();
  const visibleEvents = Array.from({ length: 4 }, (_, index) => {
    const eventIndex = ((eventSlice - index) % EVENTS.length + EVENTS.length) % EVENTS.length;
    const time = timeFmt.format(new Date(nowMs - (index * 7 + 3) * 1000));
    return { ...EVENTS[eventIndex]!, time };
  });

  const eventLines = visibleEvents.map((event) => {
    const levelColor =
      event.level === "warn"
        ? C.yellow
        : event.level === "err"
          ? C.red
          : C.brightBlack;
    return lines([[
      { text: ` ${event.time} `, color: C.brightBlack },
      { text: pad(event.level, 4), color: levelColor },
      { text: ` ${event.msg}` },
    ]]);
  });

  return vstack(
    raw(header, C.brightBlack),
    separator(),
    hstack([
      box(
        vstack(
          raw(" 8 cores"),
          raw(` ${bar(cpuPct, 100, 17)} ${cpuPct}%`),
        ),
        { title: "cpu" },
      ),
      box(
        vstack(
          raw(` ${toFixed(memUsed, 1)}/16 GB`),
          raw(` ${bar(memUsed, 16, 17)} ${Math.round((memUsed / 16) * 100)}%`),
        ),
        { title: "mem" },
      ),
      box(
        vstack(
          raw(` ${Math.round(dskUsed)}/512 GB`),
          raw(` ${bar(dskUsed, 512, 17)} ${Math.round((dskUsed / 512) * 100)}%`),
        ),
        { title: "disk" },
      ),
    ], { gap: 1, widths: [26, 26, 26] }),
    hstack([
      box(
        vstack(
          raw(` \u25b2 ${spark(sp1, 24)}  eth0 \u25cf up`),
          raw(` \u25bc ${spark(sp2, 24)}  ${toFixed(Math.max(180, Math.round(730 + Math.cos(pulse / 4) * 190)) / 1000, 2)} Gbps`),
        ),
      ),
      box(
        vstack(
          raw(` ${okSvc}/5 healthy`),
          raw(` ${incident ? "\u25c6 degraded" : "\u25c6 stable"}`, incident ? C.yellow : C.green),
        ),
        { title: "cluster" },
      ),
    ], { gap: 1, widths: [53, 26] }),
    blank(),
    table(SERVICE_COLUMNS, serviceData, { headerColor: C.yellow }),
    blank(),
    separator(),
    ...eventLines,
    statusbar(
      [{ text: ` \u25cf ${okSvc}/5 svc  \u2191 0.92 Gbps  \u25c6 ${incident ? "degraded" : "stable"}` }],
      [{ text: "v0.1.0 " }],
      { bg: "#1a1a2e", color: C.white },
    ),
  );
}

export function mountHero(term: DemoTermElement): () => void {
  let tick = 0;

  const render = () => {
    setTermLayout(term, buildHeroLayout(tick));
  };

  render();

  const timer = setInterval(() => {
    tick += 1;
    render();
  }, 200);

  return () => {
    clearInterval(timer);
  };
}
